import React, { useState, useEffect, useRef, useCallback } from 'react';
import { GoogleMap, useJsApiLoader, Marker } from '@react-google-maps/api';
import { FaTruck, FaPlus, FaEdit, FaTrash, FaTimes, FaSave, FaGasPump, FaMapMarkerAlt, FaCrosshairs, FaQrcode, FaDownload, FaCamera } from 'react-icons/fa';
import { QRCodeCanvas } from 'qrcode.react';
import { useModal } from '../components/Modal';
import { useToast } from '../components/Toast';
import { useApiCall } from '../hooks/useApiCall';
import { useAccount } from '../contexts/AccountContext';
import '../styles/Tankers.css';

const APRON_CENTER = { lat: 5.811487489669329, lng: -55.1971283614614 };
const MAP_OPTIONS  = { mapTypeId: 'satellite', tilt: 0, zoomControl: true, mapTypeControl: false, streetViewControl: false, rotateControl: false, fullscreenControl: false };

// ─── Tanker QR + Image Panel ──────────────────────────────────────────────────
function TankerQRPanel({ tanker, onImageUploaded }) {
  const { account } = useAccount();
  const { showToast } = useToast();
  const qrRef   = useRef(null);
  const fileRef = useRef(null);
  const [imgUrl, setImgUrl]       = useState(tanker.image_url);
  const [uploading, setUploading] = useState(false);
  const [dragging, setDragging]   = useState(false);

  const downloadQR = () => {
    const canvas = qrRef.current?.querySelector('canvas');
    if (!canvas) return;
    const link = document.createElement('a');
    link.download = `QR_${tanker.name.replace(/\s+/g,'_')}.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();
  };

  const uploadFile = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append('id', tanker.id);
    fd.append('image', file);
    const API_BASE_URL = process.env.REACT_APP_API_URL;
    const API_KEY      = process.env.REACT_APP_API_KEY;
    try {
      const res = await fetch(`${API_BASE_URL}admin/tankers/upload-image`, {
        method: 'POST',
        headers: { api: API_KEY, ...(account?.token && { auth: account.token }) },
        body: fd,
      }).then(r => r.json());
      if (res?.success) {
        setImgUrl(res.data.image_url + '?t=' + Date.now());
        onImageUploaded?.();
        showToast('Image uploaded.', 'success');
      } else {
        showToast(res?.response || 'Upload failed.', 'error');
      }
    } catch { showToast('Upload error.', 'error'); }
    setUploading(false);
  };

  const handleFileInput = (e) => uploadFile(e.target.files?.[0]);

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file && file.type.startsWith('image/')) uploadFile(file);
  };

  return (
    <div className="qr-panel">
      <div className="qr-image-section">
        <div
          className={`qr-dropzone${dragging ? ' qr-dropzone--over' : ''}${uploading ? ' qr-dropzone--loading' : ''}`}
          onClick={() => !uploading && fileRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={handleDrop}
        >
          {uploading ? (
            <div className="qr-drop-placeholder">
              <div className="qr-spinner" />
              <span>Uploading…</span>
            </div>
          ) : imgUrl ? (
            <>
              <img src={imgUrl} alt={tanker.name} className="qr-preview-img" />
              <div className="qr-drop-overlay">
                <FaCamera size={22} />
                <span>Replace image</span>
              </div>
            </>
          ) : (
            <div className="qr-drop-placeholder">
              <FaCamera size={28} />
              <span>{dragging ? 'Drop to upload' : 'Drag & drop or click'}</span>
              <small>JPEG, PNG, WEBP — max 5 MB</small>
            </div>
          )}
        </div>
        <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleFileInput} />
      </div>
      <div className="qr-code-section" ref={qrRef}>
        <p className="qr-label">{tanker.name}</p>
        <p className="qr-sub">Token: <code>{tanker.qr_token}</code></p>
        <QRCodeCanvas value={tanker.qr_token} size={180} level="M" includeMargin />
        <button className="btn btn-sm btn-secondary qr-dl-btn" onClick={downloadQR}>
          <FaDownload /> Download QR
        </button>
      </div>
    </div>
  );
}

function LocationPicker({ coords, onChange }) {
  const { isLoaded } = useJsApiLoader({ googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '' });
  const [open, setOpen] = useState(false);
  const mapRef          = useRef(null);
  const initialCenter   = useRef(coords || APRON_CENTER); // never changes after mount → no re-center jerk
  const handleLoad      = useCallback((map) => { mapRef.current = map; }, []);

  const handleMapClick = useCallback((e) => {
    const pos = { lat: e.latLng.lat(), lng: e.latLng.lng() };
    onChange(pos); // apply immediately — no confirm step
  }, [onChange]);

  const handleGps = () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const p = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        onChange(p);
        mapRef.current?.panTo(p);
      },
      () => {}
    );
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange(null);
  };

  const handleOpen = () => {
    // If we already have coords, pan to them when opening
    if (coords && mapRef.current) mapRef.current.panTo(coords);
    setOpen(true);
  };

  return (
    <div className="form-row">
      <label className="form-label">GPS Location</label>
      <div className="loc-picker-bar">
        {coords
          ? <span className="loc-coords-display"><FaMapMarkerAlt className="loc-pin-icon" />{coords.lat.toFixed(6)}, {coords.lng.toFixed(6)}</span>
          : <span className="loc-no-coords">No location set</span>
        }
        <div className="loc-picker-actions">
          {coords && <button type="button" className="btn btn-sm btn-ghost loc-clear-btn" onClick={handleClear} title="Clear location"><FaTimes /></button>}
          <button type="button" className="btn btn-sm btn-secondary" onClick={handleOpen}><FaMapMarkerAlt /> {coords ? 'Change' : 'Pick on Map'}</button>
        </div>
      </div>

      {open && (
        <div className="loc-map-panel">
          <div className="loc-map-toolbar">
            <span className="loc-map-hint">{coords ? `${coords.lat.toFixed(5)}, ${coords.lng.toFixed(5)}` : 'Tap the map to drop a pin'}</span>
            <div style={{ display: 'flex', gap: 6 }}>
              <button type="button" className="btn btn-sm btn-secondary" onClick={handleGps} title="Use my current location"><FaCrosshairs /> My location</button>
              <button type="button" className="btn btn-sm btn-ghost"     onClick={() => setOpen(false)}><FaTimes /> Close</button>
            </div>
          </div>
          {isLoaded
            ? <GoogleMap
                mapContainerClassName="loc-map-container"
                center={initialCenter.current}
                zoom={18}
                options={MAP_OPTIONS}
                onLoad={handleLoad}
                onClick={handleMapClick}
              >
                {coords && <Marker position={coords} />}
              </GoogleMap>
            : <div className="loc-map-loading">Loading map…</div>
          }
        </div>
      )}
    </div>
  );
}

function TankerForm({ tanker, gasTypes, onSave, closeModal }) {
  const defaultGasTypeId = gasTypes.length > 0 ? gasTypes[0].id : 1;
  const initCoords = tanker?.lat != null ? { lat: tanker.lat, lng: tanker.lng } : null;
  const [form, setForm] = useState(tanker
    ? { name: tanker.name, qr_token: tanker.qr_token, gas_type_id: tanker.gas_type_id, amount_cap: tanker.amount_cap, amount: tanker.amount, amount_type: tanker.amount_type, location: tanker.location, lat: tanker.lat ?? null, lng: tanker.lng ?? null }
    : { name: '', qr_token: '', gas_type_id: defaultGasTypeId, amount_cap: 1000, amount: 0, amount_type: 'L', location: '', lat: null, lng: null }
  );
  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const handleCoordsChange = (coords) => {
    setForm(prev => ({ ...prev, lat: coords?.lat ?? null, lng: coords?.lng ?? null }));
  };

  const coords = form.lat != null ? { lat: form.lat, lng: form.lng } : null;

  return (
    <form id="tanker-form" onSubmit={e => { e.preventDefault(); onSave({ ...form, id: tanker?.id || 0 }); closeModal(); }}>
      <div className="form-row-2">
        <div className="form-row">
          <label className="form-label">Tanker name</label>
          <input className="form-input" value={form.name} onChange={e => set('name', e.target.value)} required placeholder="e.g. Tanker D" />
        </div>
        <div className="form-row">
          <label className="form-label">QR Token</label>
          <input className="form-input" value={form.qr_token} onChange={e => set('qr_token', e.target.value)} placeholder="uas_avgas (auto if empty)" />
        </div>
      </div>
      <div className="form-row-2">
        <div className="form-row">
          <label className="form-label">Fuel type</label>
          <select className="form-input" value={form.gas_type_id} onChange={e => set('gas_type_id', Number(e.target.value))}>
            {gasTypes.map(g => <option key={g.id} value={g.id}>{g.type}</option>)}
          </select>
        </div>
        <div className="form-row">
          <label className="form-label">Unit</label>
          <select className="form-input" value={form.amount_type} onChange={e => set('amount_type', e.target.value)}>
            <option value="L">L</option>
            <option value="Gal">Gal</option>
          </select>
        </div>
      </div>
      <div className="form-row-2">
        <div className="form-row">
          <label className="form-label">Capacity ({form.amount_type})</label>
          <input className="form-input" type="number" value={form.amount_cap} onChange={e => set('amount_cap', Number(e.target.value))} min={1} required />
        </div>
        <div className="form-row">
          <label className="form-label">Current level ({form.amount_type})</label>
          <input className="form-input" type="number" value={form.amount} onChange={e => set('amount', Number(e.target.value))} min={0} max={form.amount_cap} required />
        </div>
      </div>
      <div className="form-row">
        <label className="form-label">Location label</label>
        <input className="form-input" value={form.location} onChange={e => set('location', e.target.value)} placeholder="Apron 1, Hangar 3…" />
      </div>
      <LocationPicker coords={coords} onChange={handleCoordsChange} />
    </form>
  );
}

function DeleteConfirm({ tanker, onDelete, closeModal }) {
  return (
    <div className="delete-confirmation">
      <p>Delete <strong>{tanker.name}</strong>?</p>
      <p className="warning-text">This cannot be undone.</p>
    </div>
  );
}

export default function Tankers() {
  const apiCall = useApiCall();
  const [tankers, setTankers] = useState([]);
  const [gasTypes, setGasTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const { openModal } = useModal();
  const { success, showToast } = useToast();

  const loadTankers = () => {
    apiCall('admin/tankers', 'POST').then(res => {
      if (res?.success) {
        setTankers(res.data.tankers || []);
        setGasTypes(res.data.gas_types || []);
      }
      setLoading(false);
    });
  };

  useEffect(() => { loadTankers(); }, []);

  const handleSave = (form) => {
    apiCall('admin/tankers/update', 'POST', { form }).then(res => {
      if (res?.success) {
        success(form.id > 0 ? `${form.name} updated` : `${form.name} created`);
        loadTankers();
      } else {
        showToast(res?.response || 'Failed to save tanker', 'error');
      }
    });
  };

  const handleDelete = (id) => {
    apiCall('admin/tankers/delete', 'POST', { id }).then(res => {
      if (res?.success) {
        success('Tanker removed');
        loadTankers();
      } else {
        showToast(res?.response || 'Failed to delete tanker', 'error');
      }
    });
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1>Tankers</h1>
          <p className="page-subtitle">Manage your fuel bowser fleet</p>
        </div>
        <button className="btn btn-primary" onClick={() => openModal('Add tanker', <TankerForm gasTypes={gasTypes} onSave={handleSave} />, 'md', {
          closeOnBackdrop: false,
          footer: (closeModal) => (
            <>
              <button type="button" className="btn btn-secondary" onClick={closeModal}><FaTimes /> Cancel</button>
              <button type="submit" form="tanker-form" className="btn btn-primary"><FaSave /> Add tanker</button>
            </>
          )
        })}>
          <FaPlus /> Add tanker
        </button>
      </div>

      {loading ? (
        <div className="loading-state">Loading...</div>
      ) : (
        <div className="tankers-grid">
          {tankers.map(t => {
            const pct = t.level_pct;
            const barColor = pct < 20 ? '#ef4444' : pct < 40 ? '#f59e0b' : '#22c55e';
            return (
              <div key={t.id} className="tanker-card">
                <div className="tanker-card-header">
                  <div className="tanker-card-title">
                    <FaTruck />
                    <span>{t.name}</span>
                  </div>
                  <div className={`tanker-status-badge ${t.in_use ? 'in-use' : 'available'}`}>
                    {t.in_use ? 'In Use' : 'Available'}
                  </div>
                </div>

                <div className="tanker-card-meta">
                  <div><FaGasPump /> {t.fuelType}</div>
                  <div>Location: {t.location || '-'}</div>
                  <div>QR: {t.qr_token}</div>
                </div>

                <div className="fl-bar-section">
                  <div className="fuel-bar-track large">
                    <div className="fuel-bar-fill" style={{ width: `${pct}%`, background: barColor }} />
                  </div>
                  <div className="fl-bar-labels">
                    <span style={{ color: barColor, fontWeight: 700 }}>{pct}%</span>
                    <span className="fl-gal">{t.amount} / {t.amount_cap} {t.amount_type}</span>
                  </div>
                </div>

                <div className="tanker-card-actions">
                  <button className="btn btn-sm btn-secondary" onClick={() => openModal(`${t.name} — QR & Image`, <TankerQRPanel tanker={t} onImageUploaded={loadTankers} />, 'md')}>
                    <FaQrcode /> QR
                  </button>
                  <button className="btn btn-sm btn-secondary" onClick={() => openModal(`Edit ${t.name}`, <TankerForm tanker={t} gasTypes={gasTypes} onSave={handleSave} />, 'md', {
                    closeOnBackdrop: false,
                    footer: (closeModal) => (
                      <>
                        <button type="button" className="btn btn-secondary" onClick={closeModal}><FaTimes /> Cancel</button>
                        <button type="submit" form="tanker-form" className="btn btn-primary"><FaSave /> Save changes</button>
                      </>
                    )
                  })}>
                    <FaEdit /> Edit
                  </button>
                  <button className="btn btn-sm btn-danger" onClick={() => openModal('Delete tanker', <DeleteConfirm tanker={t} onDelete={handleDelete} />, 'sm', {
                    footer: (closeModal) => (
                      <>
                        <button className="btn btn-secondary" onClick={closeModal}>Cancel</button>
                        <button className="btn btn-primary" onClick={() => { handleDelete(t.id); closeModal(); }}>Delete</button>
                      </>
                    )
                  })}>
                    <FaTrash />
                  </button>
                </div>
              </div>
            );
          })}
          {tankers.length === 0 && <div className="empty-state">No tankers found</div>}
        </div>
      )}
    </div>
  );
}
